import type { CertificateExpiryStatus } from '../types/certificate.types';

const YELLOW_THRESHOLD_DAYS = 90;

export interface CertificateExpirySummary {
  status: CertificateExpiryStatus;
  /** La fecha de vencimiento mas proxima entre Auth y Sign, o null si no hay ninguna fecha. */
  nearestExpiresAt: Date | null;
}

function daysUntil(date: Date, now: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((date.getTime() - now.getTime()) / msPerDay);
}

/**
 * Calcula el estado del semaforo de vencimiento de certificado (verde/amarillo/rojo)
 * a partir de las fechas de vencimiento de los certificados Auth y Sign.
 *
 * Umbral (decidido, ver 00-loop-prompt.md punto 4a):
 * - Verde: la fecha mas proxima esta a mas de 90 dias.
 * - Amarillo: la fecha mas proxima esta a 90 dias o menos, y aun no vencio.
 * - Rojo: la fecha mas proxima ya paso.
 * - Verde (sin fechas): si no hay ninguna fecha de vencimiento disponible (ambas null),
 *   no se puede advertir de un vencimiento que no se conoce -- se trata como verde.
 */
export function getCertificateExpiryStatus(
  authCertExpiresAt: string | null,
  signCertExpiresAt: string | null,
  now: Date = new Date()
): CertificateExpirySummary {
  const dates = [authCertExpiresAt, signCertExpiresAt]
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (dates.length === 0) {
    return { status: 'green', nearestExpiresAt: null };
  }

  const nearestExpiresAt = dates.reduce((nearest, current) =>
    current.getTime() < nearest.getTime() ? current : nearest
  );

  const daysRemaining = daysUntil(nearestExpiresAt, now);

  if (daysRemaining < 0) {
    return { status: 'red', nearestExpiresAt };
  }

  if (daysRemaining <= YELLOW_THRESHOLD_DAYS) {
    return { status: 'yellow', nearestExpiresAt };
  }

  return { status: 'green', nearestExpiresAt };
}

/**
 * Texto legible de "cuanto falta / cuanto lleva vencido" para acompañar el
 * badge y el pop-up -- sin esto, Amarillo y Rojo solo mostraban la fecha
 * absoluta y el usuario tenia que calcular mentalmente la urgencia.
 * Devuelve null en Verde (no hay nada que advertir).
 */
export function formatExpiryRelative(
  status: CertificateExpiryStatus,
  nearestExpiresAt: Date | null,
  now: Date = new Date()
): string | null {
  if (status === 'green' || !nearestExpiresAt) {
    return null;
  }

  const days = Math.abs(daysUntil(nearestExpiresAt, now));

  if (status === 'red') {
    return days === 0 ? 'Vencido hoy' : `Vencido hace ${days} ${days === 1 ? 'día' : 'días'}`;
  }

  return days === 0 ? 'Vence hoy' : `Vence en ${days} ${days === 1 ? 'día' : 'días'}`;
}
